import { useInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataList, DetailPanel, DetailPlaceholder, type Column } from "@/components/DataList";
import type { ListTools } from "@/components/ListToolbar";
import { DiscountPanel } from "@/features/discounts/DiscountPanel";
import { discountKeys, discountListQuery, discountScopes, isDiscountScope, listDiscounts, type DiscountRow, type DiscountScope, type ListParams } from "@/features/discounts/api";
import { ImportPanel } from "@/features/import/ImportPanel";
import { activeCompanyOf, useActiveCompany } from "@/lib/activeCompany";
import { cn } from "@/lib/cn";
import { EXPORT_PAGE, type ExportColumn } from "@/lib/exportXlsx";
import { useFormat } from "@/lib/format";
import { sessionContextQuery, useCan } from "@/lib/session";

// Sales → Master data → Discounts (PX-SAL-330, ADR-052): the active company's
// discount rules (customer × item or whole range × minimum × validity),
// keyset through sales_discount_page; the detail edits the rule from the list
// row (no second round-trip). Import (entity sales_discounts) next to Export
// (ADR-034).

interface Search {
  scope: DiscountScope;
  q: string;
  d?: string | undefined;
  import?: "sales_discounts" | undefined;
}

export const Route = createFileRoute("/_app/sales/discounts")({
  validateSearch: (raw: Record<string, unknown>): Search => {
    const s: Search = { scope: isDiscountScope(raw.scope) ? raw.scope : "current", q: typeof raw.q === "string" ? raw.q.slice(0, 40) : "" };
    if (typeof raw.d === "string" && raw.d && raw.d.length <= 64) s.d = raw.d;
    if (raw.import === "sales_discounts") s.import = raw.import;
    return s;
  },
  loaderDeps: ({ search }) => ({ scope: search.scope, q: search.q }),
  loader: async ({ context, deps }) => {
    const ctx = await context.queryClient.ensureQueryData(sessionContextQuery);
    const companyId = activeCompanyOf(ctx)?.id;
    if (companyId) await context.queryClient.ensureInfiniteQueryData(discountListQuery({ companyId, scope: deps.scope, q: deps.q }));
  },
  component: DiscountsPage,
});

function DiscountsPage() {
  const { t } = useTranslation();
  const tr = t as unknown as (key: string) => string;
  const fmt = useFormat();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { company } = useActiveCompany();
  const canManage = useCan("sales.price.manage");
  const canImport = useCan("core.data.import") && canManage;
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );
  const params: ListParams = { companyId: company?.id ?? "", scope: search.scope, q: search.q };
  const query = useInfiniteQuery(discountListQuery(params));
  const rows = query.data?.pages.flatMap((p) => p.rows) ?? [];
  const selected = search.d && search.d !== "new" ? (rows.find((r) => r.id === search.d) ?? null) : null;
  const [text, setText] = useState(search.q);
  useEffect(() => {
    if (text === search.q) return;
    const id = setTimeout(() => setSearch({ q: text }), 200);
    return () => clearTimeout(id);
  }, [text, search.q, setSearch]);

  const columns: Column<DiscountRow>[] = [
    {
      key: "customer",
      header: t("discounts.customer"),
      width: "minmax(170px,1.2fr)",
      cell: (r, sel) => (
        <span className="truncate">
          <span className={cn("font-mono text-xs", sel && "font-semibold text-accent-ink")}>{r.customer_code}</span>{" "}
          <span className="text-ink-soft">{r.customer_name}</span>
        </span>
      ),
    },
    {
      key: "item",
      header: t("discounts.item"),
      width: "minmax(170px,1.2fr)",
      cell: (r) =>
        r.item_code ? (
          <span className="truncate">
            <span className="font-mono text-xs">{r.item_code}</span> <span className="text-ink-soft">{r.item_description}</span>
          </span>
        ) : (
          <span className="italic text-ink-muted">{t("discounts.allItems")}</span>
        ),
    },
    { key: "pct", header: t("discounts.pct"), width: "minmax(70px,0.4fr)", align: "end", cell: (r) => <span className="tabular">{Number(r.discount_pct)} %</span> },
    {
      key: "min",
      header: t("discounts.minQty"),
      width: "minmax(90px,0.5fr)",
      align: "end",
      cell: (r) => <span className="tabular">{Number(r.min_qty) > 0 ? `${Number(r.min_qty)} ${r.base_uom_code}` : "—"}</span>,
    },
    {
      key: "validity",
      header: t("discounts.validity"),
      width: "minmax(170px,1fr)",
      className: "tabular text-ink-soft",
      cell: (r) => `${fmt.day(r.valid_from)} – ${r.valid_to ? fmt.day(r.valid_to) : t("priceLists.openEnded")}`,
    },
  ];
  const exportColumns: ExportColumn<DiscountRow>[] = [
    { header: "customer", kind: "code", value: (r) => r.customer_code },
    { header: "item_code", kind: "code", value: (r) => r.item_code },
    { header: "discount_pct", kind: "number", value: (r) => String(r.discount_pct) },
    { header: "min_qty", kind: "number", value: (r) => String(r.min_qty) },
    { header: "valid_from", kind: "date", value: (r) => r.valid_from },
    { header: "valid_to", kind: "date", value: (r) => r.valid_to },
  ];
  const listTools: ListTools<DiscountRow> = {
    id: "sales-discounts",
    title: t("discounts.title"),
    onNew: canManage && company ? () => setSearch({ d: "new", import: undefined }) : undefined,
    onImport: canImport ? () => setSearch({ import: "sales_discounts", d: undefined }) : undefined,
    export: { fileBase: "sales-discounts", sheet: t("discounts.title"), columns: exportColumns, fetchPage: (cursor, signal) => listDiscounts(params, cursor, signal, EXPORT_PAGE) },
  };
  const chip = (pressed: boolean) =>
    cn(
      "h-8 rounded-full border px-3 text-[13px] transition-colors",
      pressed ? "border-ink bg-ink font-medium text-surface" : "border-border bg-surface text-ink hover:bg-surface-hover",
    );

  return (
    <div className="flex min-h-0 grow flex-col gap-4">
      <header className="flex flex-wrap items-end gap-3">
        <div className="flex grow flex-col gap-0.5">
          <nav aria-label="Breadcrumb" className="text-[13px] text-ink-muted">
            {t("nav.sales.title")} <span aria-hidden="true">/</span> {t("nav.sales.discounts")}
          </nav>
          <h1 className="text-[28px] font-semibold leading-9 tracking-[-0.02em]">{t("discounts.title")}</h1>
        </div>
        {canManage && company && (
          <button
            type="button"
            onClick={() => setSearch({ d: "new", import: undefined })}
            className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-accent pl-3 pr-4 font-medium text-on-accent hover:bg-accent-hover"
          >
            <Plus size={18} strokeWidth={2} aria-hidden="true" />
            {t("discounts.new")}
          </button>
        )}
      </header>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label={t("discounts.validity")} className="flex flex-wrap gap-2">
          {discountScopes.map((s) => (
            <button key={s} type="button" aria-pressed={search.scope === s} onClick={() => setSearch({ scope: s })} className={chip(search.scope === s)}>
              {tr(`discounts.scope.${s}`)}
            </button>
          ))}
        </div>
        <input
          type="search"
          aria-label={t("discounts.search")}
          placeholder={t("discounts.search")}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="ml-auto h-8 w-56 rounded-lg border border-border bg-surface px-2.5 text-[13px] placeholder:text-ink-muted"
        />
      </div>
      <div className="flex min-h-0 grow gap-4">
        <DataList
          label={t("discounts.title")}
          columns={columns}
          query={query}
          rowId={(r) => r.id}
          selectedId={search.d}
          onSelect={(r) => setSearch({ d: r.id, import: undefined })}
          onClear={() => setSearch({ d: undefined })}
          emptyTitle={t("discounts.emptyTitle")}
          emptyBody={t("discounts.emptyBody")}
          emptyAction={
            canManage && company ? (
              <button
                type="button"
                onClick={() => setSearch({ d: "new" })}
                className="h-9 rounded-xl bg-accent px-3.5 text-[13px] font-medium text-on-accent hover:bg-accent-hover"
              >
                {t("discounts.new")}
              </button>
            ) : undefined
          }
          minWidth={700}
          tools={listTools}
        />
        <DetailPanel
          label={search.import ? t("import.title") : t("discounts.detailLabel")}
          open={!!search.d || !!search.import}
          placeholder={<DetailPlaceholder title={t("discounts.selectPrompt")} hint={t("discounts.selectPromptHint")} />}
        >
          {search.import ? (
            <ImportPanel
              entities={["sales_discounts"]}
              initial="sales_discounts"
              companyId={company?.id}
              onClose={() => setSearch({ import: undefined })}
              invalidate={[discountKeys.all]}
            />
          ) : (
            search.d &&
            company &&
            (search.d === "new" || selected) && (
              <DiscountPanel
                key={search.d}
                row={selected}
                companyId={company.id}
                onClose={() => setSearch({ d: undefined })}
                onSaved={(id) => setSearch({ d: id ?? undefined })}
              />
            )
          )}
        </DetailPanel>
      </div>
    </div>
  );
}
